'use client'

import type { GlobalChrome } from '@/utils/getGlobalChrome'

import { useEffect } from 'react'
import AOS from 'aos'
import dynamic from 'next/dynamic'

import MainFooter from '@/components/footers/MainFooter'
import SubFooter from '@/components/footers/SubFooter'
// Header and footers are part of the page chrome — they MUST be in the static
// HTML so they paint with the content (no post-hydration flicker). They are
// SSR-safe: all browser-API access is inside effects/handlers, the nav menu is
// derived synchronously from the pathname, and Zustand uses its initial state
// on the server.
import MainHeader from '@/components/headers/MainHeader'
// Renders the Maintenance panel (plus a retry) if any route below it throws.
// Unlike an app/error.tsx boundary it leaves notFound()/redirect() alone.
import ErrorBoundary from '@/components/maintenance/ErrorBoundary'
import { useDescriptiveLinks } from '@/hooks/useDescriptiveLinks'
import { useInertHiddenSlides } from '@/hooks/useInertHiddenSlides'
import { useInternalLinkNavigation } from '@/hooks/useInternalLinkNavigation'
import usePageTracking from '@/hooks/usePageTracking'
import { installBreadcrumbs } from '@/utils/breadcrumbs'
import { GlobalChromeProvider } from './GlobalChromeContext'

// Overlays/modals are conditionally shown after mount, so they don't belong in
// the initial paint — keep them client-only to avoid hydration churn.
const PromotionalBanner = dynamic(() => import('@/components/modal/PromotionalBanner'), {
  ssr: false
})
const SecurityTips = dynamic(() => import('@/components/modal/SecurityTips'), { ssr: false })
const GlobalAlert = dynamic(() => import('@/components/alert/GlobalAlert'), { ssr: false })

type PromotionNavItem = { title: string; path: string }

export default function AppShell({
  children,
  promotionNavItems = [],
  chrome
}: {
  children: React.ReactNode
  promotionNavItems?: PromotionNavItem[]
  /**
   * Site-wide CMS chrome resolved at build time by layout.tsx. Forwarded to the
   * alert/popup/teaser as `initialData` and published on a context for the 54
   * LetsConnect call sites, replacing four mount-time CMS fetches per page load.
   */
  chrome: GlobalChrome
}) {
  usePageTracking()
  useInternalLinkNavigation()
  useInertHiddenSlides()
  useDescriptiveLinks()

  useEffect(() => {
    AOS.init()
  }, [])

  // Starts the click/failed-request trail that rides along with any crash
  // report (see utils/breadcrumbs.ts). Installed once, as early as the shell
  // mounts, so the trail already has content by the time something throws.
  useEffect(() => {
    installBreadcrumbs()
  }, [])

  return (
    <GlobalChromeProvider value={chrome}>
      <div className="App">
        {/* First focusable element on every route — lets keyboard/SR users bypass
          the mega-menu and jump straight to content (WCAG 2.4.1 Bypass Blocks).
          Styling lives in src/styles/index.css (.skip-link). */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <SecurityTips />
        <PromotionalBanner initialData={chrome.homepagePopup} />
        <GlobalAlert initialData={chrome.globalAlert} />
        <MainHeader promotionNavItems={promotionNavItems} festivesTeaser={chrome.festivesTeaser} />
        <main id="main-content" tabIndex={-1}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
        <MainFooter />
        <SubFooter />
      </div>
    </GlobalChromeProvider>
  )
}
